import { metricConfig } from '../data/metrics/metricConfigBuilder';
import { getFormattedValue } from './formatterUtil';

const isNotValidValue = value => {
	return (
		value === null ||
		value === undefined ||
		value === '-9999999999999' ||
		value === 'NaN' ||
		value === '' ||
		value.toLocaleString() === 'NaN' ||
		value === 'N/A' ||
		value === '-'
	);
};

const returnValidValForMetric = (metricResObj, col) => {
	if (
		metricResObj.metric !== 'ObjectName' &&
		metricResObj.metric === col.id &&
		metricResObj.period === col.tp
	) {
		const val = isNotValidValue(metricResObj.value)
			? '--'
			: metricResObj.value;
		return val;
	}
	return null;
};

export const metricsResFormatToExcelData = (
	inputData,
	accountsMap,
	data,
	headers,
	columnHeaders,
) => {
	const result = []; // results is two dimensional array and valueMap represents rows in results.

	const accountGroupHeaders = columnHeaders.filter(
		i =>
			i.header === 'Account Breakdown' ||
			(!i.isBenchmark && !i.isAttribution),
	);
	const benchMarkHeaders = columnHeaders.filter(i => i.isBenchmark);
	const attributionHeaders = columnHeaders.filter(i => i.isAttribution);

	data.rows.forEach(row => {
		const { cubeDimensionValues: cubeValues, datasets } = row;
		const valueMap = [];
		const shouldAddEmptyVal = datasets.length === 1;

		datasets.forEach(dataset => {
			const { name, periodResults } = dataset;
			const isSourcePortfolio = name === 'sourcePortfolio';
			const isTargetBenchmark = name === 'targetBenchmark';
			const shouldAddBenchmarkVal =
				shouldAddEmptyVal && isSourcePortfolio;
			const shouldAddAccountVal = shouldAddEmptyVal && isTargetBenchmark;

			// Source Portfolio: Account Group Columns
			if (isSourcePortfolio) {
				accountGroupHeaders.forEach(col => {
					if (col.id === 'Account Breakdown') return;
					const hasMetric = periodResults.some(
						i => i.metric === col.id,
					);
					// NOTE: if selected metrics doesnt return in response add '--' to that column e.g info metrics (SEDOL, CUSIP)
					if (!hasMetric) {
						valueMap.push('--');
					} else {
						periodResults.forEach(period => {
							const val = returnValidValForMetric(period, col);
							if (val) valueMap.push(getFormattedValue(val,period.type));
						});
					}
				});
			} else if (shouldAddAccountVal) {
				// Add empty values for account group columns if no sourcePortfolio
				accountGroupHeaders.forEach(col => {
					if (col.id === 'Account Breakdown') return;
					valueMap.push('--');
				});
			}

			// Target Benchmark: Benchmark Columns
			if (isTargetBenchmark && !shouldAddBenchmarkVal) {
				benchMarkHeaders.forEach(col => {
					periodResults.forEach(period => {
						const val = returnValidValForMetric(period, col);
						if (val) valueMap.push(getFormattedValue(val,period.type));
					});
				});
			} else if (
				shouldAddBenchmarkVal &&
				inputData.benchmark.text !== 'None'
			) {
				benchMarkHeaders.forEach(() => valueMap.push('--'));
			}

			// Attribution Columns
			if (inputData.workspaceType.text === 'Attribution') {
				attributionHeaders.forEach(col => {
					periodResults.forEach(period => {
						const val = returnValidValForMetric(period, col);
						if (val) valueMap.push(getFormattedValue(val,period.type));
					});
				});
			}
		});

		// Handle Parent and Child Nodes
		if (
			cubeValues.length === 0 &&
			inputData.analysisLevel?.key !== 'Hierarchy'
		) {
			const selectedAcc = inputData.accountId.map(i => i.text).join(', ');
			result.push([selectedAcc, ...valueMap]);
		} else if (
			cubeValues.length === 0 &&
			inputData.analysisLevel?.key === 'Hierarchy' &&
			inputData.hierarchyLevel.key === 'Accounts Only') {
				const heirarchy = inputData.hierarchy.level1GroupName;
				result.push([heirarchy, ...valueMap]);
		} else {
			let indent = result.length > 0 ? '    ' : '';
			cubeValues.forEach((dim, index) => {
				if (cubeValues.length - 1 === index) {
					const val =
						dim.column === 'AccountBusinessID'
							? `${accountsMap[dim.value].accountID} - ${accountsMap[dim.value].accountLongName}`
							: dim.value;
					const name = `${indent}${val}`;
					// Avoid duplicate dimensions in tree structure
					if (result[result.length - 1]?.[0] !== name) {
						result.push([name, ...valueMap]);
					}
					indent += '    ';
				} else {
					indent += '    ';
				}
			});
		}
	});

	console.log([...headers, ...result]);
	return [...headers, ...result];
};

export function transformedTSRowsToExcelFormat(apiRows, inputData, mappings, accountsMap) {

  const grouped = {};

  // Group rows by endDate

  for (const row of apiRows) {

	const date = row.endDate;
	let target = null;
	let res = [];

    if (!grouped[date]) {
		let initialValues = new Array(mappings.length).fill(0);
		initialValues[0] = date;
		grouped[date] = initialValues;
	}

	// const source = row.datasets.find(d => d.name === "sourcePortfolio")?.periodResults[0]?.value ?? 0;

    const portfolioObj = row.datasets.find(d => d.name === "sourcePortfolio")?.periodResults[0];
	let source = 0.00;
	if(portfolioObj){
		source = getFormattedValue(portfolioObj.value,portfolioObj.type);
	}	

	if(inputData.benchmark?.benchmarkUsage !== 'None'){
		// target = row.datasets.find(d => d.name === "targetBenchmark")?.periodResults[0]?.value ?? 0;
		const benchmarkObj = row.datasets.find(d => d.name === "targetBenchmark")?.periodResults[0];
		target = 0.00;
		if(benchmarkObj){
			target = getFormattedValue(benchmarkObj.value,benchmarkObj.type);
		}
	}	

	if(row.cubeDimensionValues.length === 0){
		const accIndex = getRowIndex('Totals|Account', mappings);
		if(accIndex !== -1){
			grouped[date][accIndex] = isNotValidValue(source) ? 0 : source;
		}		
		if(target !== null){
			const benchmarkIndex = getRowIndex('Totals|Benchmark', mappings);
			if(benchmarkIndex !== -1){
				grouped[date][benchmarkIndex] = isNotValidValue(target) ? 0 : target;
			}
		}
	}

	if(row.cubeDimensionValues.length > 0){
		let headerMap = row.cubeDimensionValues.map(dim => 
				dim.column === 'AccountBusinessID' ? `${accountsMap[dim.value].accountID} - ${accountsMap[dim.value].accountLongName}` : dim.value).join('|');
		if(headerMap !== ''){
			const accountIndex = getRowIndex((headerMap+'|Account'), mappings);
			if(accountIndex !== -1){
				grouped[date][accountIndex] = isNotValidValue(source) ? 0 : source;
			}		
			if(target !== null){
				const bIndex = getRowIndex((headerMap+'|Benchmark'), mappings);
				if(bIndex !== -1){
					grouped[date][bIndex] = isNotValidValue(target) ? 0 : target;
				}
			}
		}
	}
	
    // grouped[date] = [...grouped[date],...res];
  }

  const matrix = []; // Initialize header

  // Now build rows

  for (const [date, valuePairs] of Object.entries(grouped)) {
	matrix.push(valuePairs);
  }

  return matrix.reverse();

}

export const transformedTPRowsToExcelFormat = (
	inputData,
	accountsMap,
	data,
	headers,
	columnHeaders,
) => {
	const result = []; // results is two dimensional array and valueMap represents rows in results.

	const accountGroupHeaders = columnHeaders.filter(
		i =>
			i.header === 'Account Breakdown' ||
			(!i.isBenchmark && !i.isAttribution),
	);
	const benchMarkHeaders = columnHeaders.filter(i => i.isBenchmark);
	const attributionHeaders = columnHeaders.filter(i => i.isAttribution);

	data.rows.forEach(row => {
		const { cubeDimensionValues: cubeValues, datasets } = row;
		const valueMap = [];
		const shouldAddEmptyVal = datasets.length === 1;

		datasets.forEach(dataset => {
			const { name, periodResults } = dataset;
			const isSourcePortfolio = name === 'sourcePortfolio';
			const isTargetBenchmark = name === 'targetBenchmark';
			const shouldAddBenchmarkVal =
				shouldAddEmptyVal && isSourcePortfolio;
			const shouldAddAccountVal = shouldAddEmptyVal && isTargetBenchmark;

			// Source Portfolio: Account Group Columns
			if (isSourcePortfolio) {
				accountGroupHeaders.forEach(col => {
					if (col.id === 'Account Breakdown') return;
					const hasMetric = periodResults.some(
						i => i.metric === col.id,
					);
					// NOTE: if selected metrics doesnt return in response add '--' to that column e.g info metrics (SEDOL, CUSIP)
					if (!hasMetric) {
						valueMap.push('--');
					} else {
						periodResults.forEach(period => {
							const val = returnValidValForMetric(period, col);
							if (val) valueMap.push(getFormattedValue(val,period.type));
						});
					}
				});
			} else if (shouldAddAccountVal) {
				// Add empty values for account group columns if no sourcePortfolio
				accountGroupHeaders.forEach(col => {
					if (col.id === 'Account Breakdown') return;
					valueMap.push('--');
				});
			}

			// Target Benchmark: Benchmark Columns
			if (isTargetBenchmark && !shouldAddBenchmarkVal) {
				benchMarkHeaders.forEach(col => {
					periodResults.forEach(period => {
						const val = returnValidValForMetric(period, col);
						if (val) valueMap.push(getFormattedValue(val,period.type));
					});
				});
			} else if (
				shouldAddBenchmarkVal &&
				inputData.benchmark.text !== 'None'
			) {
				benchMarkHeaders.forEach(() => valueMap.push('--'));
			}

			// Attribution Columns
			if (inputData.workspaceType.text === 'Attribution') {
				attributionHeaders.forEach(col => {
					periodResults.forEach(period => {
						const val = returnValidValForMetric(period, col);
						if (val) valueMap.push(getFormattedValue(val,period.type));
					});
				});
			}
		});

		// Handle Parent and Child Nodes
		if (
			cubeValues.length === 0 &&
			inputData.analysisLevel?.key !== 'Hierarchy'
		) {
			const selectedAcc = inputData.accountId.map(i => i.text).join(', ');
			result.push([selectedAcc, ...valueMap]);
		} else if (
			cubeValues.length === 0 &&
			inputData.analysisLevel?.key === 'Hierarchy' &&
			inputData.hierarchyLevel.key === 'Accounts Only') {
				const heirarchy = inputData.hierarchy.level1GroupName;
				result.push([heirarchy, ...valueMap]);
		} else {
			let indent = result.length > 0 ? '    ' : '';
			cubeValues.forEach((dim, index) => {
				if (cubeValues.length - 1 === index) {
					const val =
						dim.column === 'AccountBusinessID'
							? `${accountsMap[dim.value].accountID} - ${accountsMap[dim.value].accountLongName}`
							: dim.value;
					const name = `${indent}${val}`;
					// Avoid duplicate dimensions in tree structure
					if (result[result.length - 1]?.[0] !== name) {
						result.push([name, ...valueMap]);
					}
					indent += '    ';
				} else {
					indent += '    ';
				}
			});
		}
	});

	console.log([...headers, ...result]);
	return result;
}; 
 
const getRowIndex = (key , mappings) => {
	return mappings.indexOf(key);
}